import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { AlertCircle, Camera, CheckCircle2, Clock } from 'lucide-react';
import { useAuthStore } from '../auth/authStore';
import { useI18nStore } from '../../shared/i18n/i18nStore';
import { fmtMoney } from '../../shared/lib/format';
import { localClock, localDayShort } from '../../shared/lib/datetime';
import { refreshWallet } from '../../shared/wallet/refreshWallet';
import { orderStatusBucket } from '../../shared/wallet/orderStatus';
import { unpaidCardState, type UnpaidCardState } from '../../shared/wallet/unpaidDeposits';
import { syncWalletDeposit, type WalletOrder } from './api';
import { fetchDepositProofFile, fetchDepositProofs, clampProofCode, normalizeProofCode, prepareProofFile, proofErrorKey, submitDepositProof, validProofCode, type DepositProof } from './depositProofApi';
import { DEPOSIT_ORDERS_QUERY_KEY } from './useDepositOrders';
import { ProofStepper, type ProofStep } from './ProofStepper';
import { ReceiptExamplesSheet } from './ReceiptExamplesSheet';

const MAX_SUBMISSIONS = 5;

export function UnpaidDepositCard({ order, highlighted, compact = false, onCredited, onSupport, onStateChange }: {
  order: WalletOrder;
  highlighted: boolean;
  /** Inside the order detail the hero already shows the amount — hide the card's own header. */
  compact?: boolean;
  onCredited: (publicId: string) => void;
  onSupport: (publicId: string) => void;
  onStateChange?: (state: UnpaidCardState) => void;
}) {
  const t = useI18nStore((s) => s.t);
  const token = useAuthStore((s) => s.token) || '';
  const queryClient = useQueryClient();
  const publicId = String(order.publicId || '');
  const [proofs, setProofs] = useState<DepositProof[] | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [reload, setReload] = useState(0);
  const [orderStatus, setOrderStatus] = useState(String(order.status || ''));
  const [code, setCode] = useState('');
  const [file, setFile] = useState<Blob | null>(null);
  const [fileUrl, setFileUrl] = useState('');
  const [savedUrl, setSavedUrl] = useState('');
  const [savedType, setSavedType] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [exampleOpen, setExampleOpen] = useState(false);
  const requestId = useRef(crypto.randomUUID());
  const ref = useRef<HTMLElement | null>(null);

  useEffect(() => { setOrderStatus(String(order.status || '')); }, [order.status]);

  useEffect(() => {
    if (!token || !publicId) return undefined;
    let active = true;
    const load = () => fetchDepositProofs(token, publicId)
      .then((items) => { if (active) { setProofs(items); setLoadError(false); } })
      .catch(() => { if (active) setLoadError(true); });
    void load();
    const timer = window.setInterval(() => { if (document.visibilityState === 'visible') void load(); }, 30_000);
    return () => { active = false; window.clearInterval(timer); };
  }, [token, publicId, reload]);

  useEffect(() => {
    if (!file) { setFileUrl(''); return undefined; }
    const url = URL.createObjectURL(file);
    setFileUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);
  useEffect(() => () => { if (savedUrl) URL.revokeObjectURL(savedUrl); }, [savedUrl]);

  useEffect(() => {
    if (!highlighted || !ref.current) return;
    ref.current.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [highlighted]);

  const latest = proofs?.[0] || null;
  const state: UnpaidCardState = unpaidCardState(orderStatus, latest?.status);

  useEffect(() => { onStateChange?.(state); }, [state, onStateChange]);

  useEffect(() => {
    if (state !== 'credited') return undefined;
    const timer = window.setTimeout(() => onCredited(publicId), 3_000);
    return () => window.clearTimeout(timer);
  }, [state, publicId, onCredited]);

  async function choose(selected?: File) {
    if (!selected) return;
    setBusy(true); setError('');
    try { setFile(await prepareProofFile(selected)); requestId.current = crypto.randomUUID(); }
    catch (e) { setError(t(proofErrorKey(e instanceof Error ? e.message : ''))); }
    finally { setBusy(false); }
  }

  async function view(proof: DepositProof) {
    try {
      const blob = await fetchDepositProofFile(token, publicId, proof.id);
      setSavedType(blob.type); setSavedUrl(URL.createObjectURL(blob));
    } catch { setError(t('unpaidErrGeneric')); }
  }

  async function submit() {
    const trimmed = normalizeProofCode(code);
    if (trimmed && !validProofCode(trimmed)) { setError(t('unpaidErrInvalid')); return; }
    if (!trimmed && !file) { setError(t('unpaidErrRequired')); return; }
    setBusy(true); setError('');
    try {
      // Pre-check: a callback may have landed while the player typed. Never submit proof for a credited order.
      try {
        const synced = (await syncWalletDeposit(token, publicId)).item;
        if (synced && orderStatusBucket(synced.status, 'deposit') === 'completed') {
          setOrderStatus(String(synced.status || 'paid'));
          void refreshWallet(queryClient, token);
          void queryClient.invalidateQueries({ queryKey: [DEPOSIT_ORDERS_QUERY_KEY, token] });
          return;
        }
      } catch { /* sync is best-effort; the submit below is the source of truth */ }
      const items = await submitDepositProof(token, publicId, { requestId: requestId.current, referenceCode: trimmed, file });
      setProofs(items); setCode(''); setFile(null); requestId.current = crypto.randomUUID();
    } catch (e) {
      const message = e instanceof Error ? e.message : '';
      if (message === 'PROOF_ALREADY_SUBMITTED') {
        // The order already has a proof on file (a duplicate submit, or one that
        // landed from another tab/device) — this can never succeed by retrying,
        // so there is no error to show. Reload the proofs list instead: it flips
        // the card to IN REVIEW, same as if the earlier submit had won the race.
        try { setProofs(await fetchDepositProofs(token, publicId)); } catch { /* best-effort; the poll interval will catch up */ }
        setCode(''); setFile(null); requestId.current = crypto.randomUUID();
      } else {
        setError(t(proofErrorKey(message)));
      }
    } finally { setBusy(false); }
  }

  const submissions = proofs?.length || 0;
  const limitReached = submissions >= MAX_SUBMISSIONS;
  const showForm = state === 'unpaid' || state === 'resubmit';

  const meta = `${localDayShort(Number(order.createdAt || 0))} ${localClock(Number(order.createdAt || 0))} · ${publicId}`;
  const amount = fmtMoney(Number(order.requestedAmount ?? order.receivedAmount ?? 0));
  const verified = latest?.status === 'verified';
  const steps: [ProofStep, ProofStep, ProofStep] =
    state === 'unpaid' ? [{ label: t('unpaidStepPaid'), state: 'done' }, { label: t('unpaidStepProof'), sub: t('unpaidStepNow'), state: 'current' }, { label: t('unpaidStepCredited'), state: 'todo' }]
    : state === 'in_review' ? [{ label: t('unpaidStepPaid'), state: 'done' }, { label: t('unpaidStepProof'), sub: latest ? localClock(latest.createdAt) : '', state: 'done' }, { label: t('unpaidStepCredited'), sub: t(verified ? 'unpaidStepCrediting' : 'unpaidStepEta'), state: 'current' }]
    : state === 'resubmit' ? [{ label: t('unpaidStepPaid'), state: 'done' }, { label: t('unpaidStepRejected'), state: 'rejected' }, { label: t('unpaidStepCredited'), state: 'todo' }]
    : [{ label: t('unpaidStepPaid'), state: 'done' }, { label: t('unpaidStepProof'), state: 'done' }, { label: t('unpaidStepCredited'), state: 'done' }];

  return (
    <article ref={ref} className={`unpaid-card${highlighted ? ' is-highlighted' : ''}${compact ? ' is-compact' : ''}`} data-state={state} data-order={publicId}>
      {compact ? null : (
        <header className="unpaid-head">
          <strong className="unpaid-amount">{amount}</strong>
          <span className="unpaid-meta">{meta}</span>
        </header>
      )}

      <ProofStepper steps={steps} />

      {state === 'in_review' ? (
        <>
          <section className="proof-status" data-proof-status={verified ? 'verified' : 'review'}>
            <Clock size={20} aria-hidden="true" />
            <div>
              <strong>{t(verified ? 'unpaidVerifiedTitle' : 'unpaidReviewTitle')}</strong>
              <p>{t(verified ? 'unpaidVerifiedBody' : 'unpaidReviewBody')}</p>
            </div>
          </section>
          {latest ? (
            <section className="unpaid-submission">
              <h3>{t('unpaidYourSubmission')}</h3>
              <div className="unpaid-sent">
                <code className="unpaid-chip">{latest.referenceCode || '—'}</code>
                {latest.contentType ? <button type="button" className="unpaid-link" onClick={() => void view(latest)}>{t('unpaidViewReceipt')}</button> : <span>{t('unpaidReceiptMissing')}</span>}
              </div>
            </section>
          ) : null}
        </>
      ) : null}

      {state === 'resubmit' ? (
        <>
          <section className="proof-status" data-proof-status="rejected">
            <AlertCircle size={20} aria-hidden="true" />
            <div>
              <strong>{t('unpaidRejectedTitle')}</strong>
              <p>{latest?.reviewNote ? t('unpaidRejectedReason').replace('%note%', latest.reviewNote) : t('unpaidResubmitHint')}</p>
              <p>{t('unpaidRejectedAction')}</p>
            </div>
          </section>
          {latest ? (
            <section className="unpaid-submission">
              <h3>{t('unpaidYourSubmissionBefore')}</h3>
              <div className="unpaid-sent">
                <code className="unpaid-chip">{latest.referenceCode || '—'}</code>
                {latest.contentType ? <button type="button" className="unpaid-link" onClick={() => void view(latest)}>{t('unpaidViewReceipt')}</button> : <span>{t('unpaidReceiptMissing')}</span>}
              </div>
            </section>
          ) : null}
        </>
      ) : null}

      {state === 'credited' ? (
        <section className="proof-status" data-proof-status="credited">
          <CheckCircle2 size={20} aria-hidden="true" />
          <div>
            <strong>{t('unpaidCreditedTitle').replace('%amount%', amount)}</strong>
            <p>{t('unpaidCreditedBody')}</p>
          </div>
        </section>
      ) : null}

      {loadError && !proofs ? (
        <p role="alert" className="form-error">
          {t('unpaidErrGeneric')} <button type="button" className="unpaid-link" onClick={() => setReload((n) => n + 1)}>{t('retry')}</button>
        </p>
      ) : null}

      {showForm && proofs && !limitReached ? (
        <div className="unpaid-form">
          <label className="unpaid-code">
            <span className="unpaid-code-title">{t('unpaidCodeTitle')}</span>
            <span className="unpaid-code-field">
              <input className="unpaid-code-input" value={code} inputMode="text" autoComplete="off" autoCapitalize="characters" spellCheck={false} disabled={busy}
                placeholder="XXXXXXXXXXXXXXXXXXXXXX" onChange={(e) => { setCode(clampProofCode(e.target.value)); setError(''); }} />
              <em>{normalizeProofCode(code).length}/22</em>
            </span>
          </label>
          <button type="button" className="unpaid-link unpaid-example-link" onClick={() => setExampleOpen(true)}>{t('unpaidSeeExample')}</button>
          <p className="unpaid-or"><span>{t('unpaidOr')}</span></p>
          <label className="unpaid-upload">
            {fileUrl ? (
              file?.type === 'application/pdf' ? <span>PDF</span> : <img src={fileUrl} alt="" />
            ) : (
              <>
                <Camera size={20} aria-hidden="true" />
                <span>{t('unpaidUpload')}</span>
              </>
            )}
            <input type="file" accept="image/*,application/pdf" disabled={busy} onChange={(e) => { void choose(e.target.files?.[0]); e.target.value = ''; }} />
          </label>
          {fileUrl ? null : <small className="unpaid-file-hint">{t('unpaidFileHint')}</small>}
          {file ? <button type="button" className="unpaid-link" disabled={busy} onClick={() => setFile(null)}>{t('unpaidRemoveFile')}</button> : null}
          <button type="button" className="primary-button unpaid-submit" disabled={busy || (!normalizeProofCode(code) && !file)} onClick={() => void submit()}>
            {busy ? t('unpaidSending') : t(state === 'resubmit' ? 'unpaidResubmit' : 'unpaidSubmit')}
          </button>
        </div>
      ) : null}
      {showForm && limitReached ? <p role="alert" className="form-error">{t('unpaidErrLimit')}</p> : null}

      {savedUrl ? <div className="unpaid-preview">{savedType === 'application/pdf' ? <a href={savedUrl} download="comprobante.pdf">PDF · {t('unpaidViewReceipt')}</a> : <img src={savedUrl} alt={t('unpaidViewReceipt')} />}<button type="button" className="unpaid-link" onClick={() => setSavedUrl('')}>{t('close')}</button></div> : null}
      {error ? <p role="alert" className="form-error">{error}</p> : null}

      {state !== 'credited' ? (
        <button type="button" className="unpaid-link unpaid-support" onClick={() => onSupport(publicId)}>
          {t(state === 'unpaid' || state === 'resubmit' ? 'unpaidSupportLink' : 'pedidosDetailContactCta')}
        </button>
      ) : null}
      {exampleOpen ? <ReceiptExamplesSheet onClose={() => setExampleOpen(false)} onSupport={() => { setExampleOpen(false); onSupport(publicId); }} /> : null}
    </article>
  );
}
